import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMessageTemplatesQuery } from "@/pages/Private/redux/messageTemplate/messageTemplate.api";

import { FooterPopoverSection } from "../utils/editorFooter.types";

const SEARCH_DEBOUNCE_MS = 300;
const TEMPLATE_PAGE_LIMIT = 15;

interface UseTemplateFooterItemsArgs {
	search: string;
	/** Inserts the template's content at the editor cursor. */
	onInsert: (content: string) => void;
}

/** Saved message templates of the footer's "Templates" popover. */
export const useTemplateFooterItems = ({
	search,
	onInsert,
}: UseTemplateFooterItemsArgs): FooterPopoverSection[] => {
	const { t } = useTranslation();

	const [query, setQuery] = useState<string>("");

	useEffect(() => {
		const timer = setTimeout(() => setQuery(search), SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [search]);

	const { data: messageTemplates } = useGetMessageTemplatesQuery({
		page: 1,
		limit: TEMPLATE_PAGE_LIMIT,
		searchValue: query,
	});

	return useMemo(
		() => [
			{
				label: t("editorFooter.sections.templates"),
				items: (messageTemplates?.data ?? []).map((template) => ({
					key: `tpl-${template.id}`,
					name: template.name ?? "",
					meta: template.content || undefined,
					onSelect: () => onInsert(template.content ?? ""),
				})),
			},
		],
		[messageTemplates, onInsert, t]
	);
};
